import { Plus } from 'lucide-react';
import { motion } from 'motion/react';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  type DropItem,
  GridListItem,
  isFileDropItem
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { ImmersiveCard } from 'v5/patterns/collection/ImmersiveCard';
import { TileCollection } from 'v5/patterns/collection/TileCollection';
import {
  ImmersiveCardSkeleton,
  TileCollectionSkeleton
} from 'v5/patterns/collection/TileCollectionSkeleton';
import { gridLayout } from 'v5/patterns/collection/tileLayouts';
import { DragPicture } from 'v5/patterns/dnd/DragPicture';
import { cn } from 'v5/platform/dom/cn';
import { useFileInput } from 'v5/platform/react/useFileInput';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { MediaPaddedThumbnail } from './MediaPaddedThumbnail';
import { MediaThumbnail } from './MediaThumbnail';
import { MediaDropZone } from './MediaDropZone';
import { MediaLightbox } from './MediaLightbox';
import { mediaKindOf } from './uploadedMedia';
import { getFilesFromClipboardData } from 'v5/platform/dom/getFilesFromClipboardData';
import { isTypingTarget } from 'v5/platform/dom/isTypingTarget';
import { PendingTile, uploadTileKey } from './PendingTile';
import type { PendingUpload } from './useMediaUploads';

/** A file the gallery shows; the host keeps what it stands for. */
export type MediaGalleryItem = {
  /** Stable across renders and unique in the gallery. */
  key: string;
  name: string;
  mimeType?: string;
  /** The image to show, or the file to open when it is no image. */
  src: string;
  /** A picture standing for a file that is no image, such as a video's frame. */
  poster?: string;
  /** The picture's original, shown when its resized copy doesn't load. */
  fallbackSrc?: string;
  /** The picture is the local copy of a fresh upload, already in memory: shown at once. */
  localCopy?: boolean;
  /** Shown in the tile's corner, e.g. a `Badge`. */
  badge?: ReactNode;
  /** Outlined, e.g. the one that stands for the others. */
  emphasized?: boolean;
  /** The host is removing it (from its own menu): the tile waits with a spinner. */
  removing?: boolean;
  /** Under the name on the caption, e.g. the file's tags. */
  caption?: ReactNode;
  /** The host's controls over the tile's corner (a menu), in place of Remove. */
  actions?: ReactNode;
};

/** Who shows a file at full size: the gallery's lightbox, or the host's own viewer. */
type MediaGalleryViewer =
  | {
      /** Opens a file in the host's viewer; the gallery shows none. */
      onOpen(key: string): void;
      thumbnailsShown?: never;
      onThumbnailsShownChange?: never;
    }
  | {
      onOpen?: never;
      /** The full-size viewer shows its strip of thumbnails; a user's preference the host keeps. */
      thumbnailsShown: boolean;
      onThumbnailsShownChange(shown: boolean): void;
    };

export type MediaGalleryProps = WithServices<[WithToastService]> &
  MediaGalleryViewer & {
    items: MediaGalleryItem[];
    /** Files on their way in (`useMediaUploads`), shown after the items. */
    uploads: PendingUpload[];
    /**
     * Takes files the gallery accepted; absent, nothing can be added.
     * `index`: where among the items they were dropped, `null` for the end.
     */
    onAddFiles?(files: File[], index: number | null): void;
    /** May take its time: the tile waits with a spinner; absent, nothing can be removed. */
    onRemove?(key: string): void | Promise<unknown>;
    /** The keys in their new order; absent, the order is fixed. */
    onReorder?(keys: string[]): void;
    onRetryUpload(upload: PendingUpload): void;
    onDismissUpload(id: string): void;
    /** File types it takes, as `<input accept>` lists them. */
    accept: string;
    /** Larger files are refused with a toast. */
    maxSize: number;
    /** `false`: one file, a new one replaces it. */
    multiple: boolean;
    /** Shown when there is nothing and nothing can be added. */
    emptyText?: ReactNode;
    /**
     * What a tile shows. Without it the immersive tile's: the picture whole on its blurred copy
     * (`MediaPaddedThumbnail`); `MediaThumbnail` fills the tile instead.
     */
    renderMedia?(item: MediaGalleryItem): ReactNode;
    /**
     * The host keeps which tiles are picked (for its batch menu); the gallery itself without it.
     * With it the tiles are also picked with a marquee, as on the shortlist board.
     */
    selectedKeys?: ReadonlySet<string>;
    onSelectedKeysChange?(keys: ReadonlySet<string>): void;
    /** Tiles at least this wide (a CSS length); 7.5rem without it. */
    tileMinWidth?: string;
    /** After the tiles: the one that picks more files, with the actions (`MediaGallery.AddTile`). */
    addTile?: ReactNode;
    /** A fixed number of columns; as many as fit without it. */
    columns?: number;
  };

/** The Add tile's key among the grid's tiles. */
const ADD_TILE = 'add-tile';

/** The tile a pointer was over, and where on it, at its last move. */
type PointerSpot = { key: string | null; clientX: number };

/** What the document paste listener needs of the gallery at the time of a paste. */
type PasteTarget = {
  items: MediaGalleryItem[];
  accepts(file: File): boolean;
  add: ((files: File[], index: number | null) => void) | null;
};

/** The media key of the tile under `target`, or null over no tile (the add tile, the empty area). */
function tileKeyAt(target: EventTarget | null): string | null {
  return target instanceof Element
    ? (target.closest<HTMLElement>('[data-media-key]')?.dataset.mediaKey ??
        null)
    : null;
}

/**
 * Where a drop or a paste at this spot lands among `items`: before the tile under the pointer, or
 * after it on its right half; null over no tile or past the last. Read at paste time, so tiles
 * moved or removed since the pointer last moved count as they stand.
 */
function landingIndexAt(
  spot: PointerSpot,
  items: MediaGalleryItem[]
): number | null {
  const at =
    spot.key === null ? -1 : items.findIndex(item => item.key === spot.key);
  const tile =
    at < 0
      ? null
      : document.querySelector<HTMLElement>(
          `[data-media-key="${CSS.escape(spot.key ?? '')}"]`
        );
  if (tile === null) return null;
  const box = tile.getBoundingClientRect();
  const landing = spot.clientX < box.left + box.width / 2 ? at : at + 1;
  return landing < items.length ? landing : null;
}

/** Whether `file` is one of the `accept` list's types (`image/*`, `application/pdf`, `.pdf`). */
export function accepts(accept: string, file: File): boolean {
  const name = file.name.toLowerCase();
  // any file at all
  if (accept.split(',').some(token => token.trim() === '*/*')) return true;
  return accept
    .split(',')
    .map(token => token.trim().toLowerCase())
    .some(token =>
      token.startsWith('.')
        ? name.endsWith(token)
        : token.endsWith('/*')
          ? file.type.startsWith(token.slice(0, -1))
          : file.type === token
    );
}

export function formatSize(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function dropItemFiles(dropItems: DropItem[]): Promise<File[]> {
  return Promise.all(
    dropItems.filter(isFileDropItem).map(item => item.getFile())
  );
}

/**
 * Files as a gallery: square thumbnails in a responsive grid; a file without
 * one shows its kind and name.
 *
 * - A click opens a file at full size. Where files can be reordered, the
 *   circle in a tile's corner selects it (Shift: a range); while any is
 *   selected, a click selects too. ⌘A selects all, Esc none, Delete removes
 *   the selection. The arrow keys move between tiles, Tab into a tile's
 *   buttons.
 * - Dragging moves the whole selection; a line between the tiles shows where
 *   it lands. From the keyboard, from a tile's handle (Enter, arrows, Enter).
 * - Files dragged in from outside land at the same line; the picker and ⌘V
 *   picker adds them at the end; ⌘V lands where the pointer is over the gallery. Each shows its progress in its
 *   own tile, and a failed one stays there with Retry until dismissed.
 *
 * Holds no files itself: hosts keep them (a form value, an entity's
 * documents) and upload with `useMediaUploads`.
 */
/** What the gallery's add tile (`AddTile`) needs of it. */
type MediaGalleryAdding = {
  openPicker(): void;
  multiple: boolean;
  /** The line after the last tile, where a drop at the end lands. */
  lastDropLine: ReactNode;
};

/** Set by a gallery that takes files; its adding pieces render nothing without it. */
const MediaGalleryAddingContext = createContext<MediaGalleryAdding | null>(
  null
);

function MediaGalleryRoot(props: MediaGalleryProps) {
  const {
    services,
    items,
    uploads,
    onAddFiles,
    onRemove,
    onReorder,
    onRetryUpload,
    onDismissUpload,
    accept,
    maxSize,
    multiple,
    emptyText,
    renderMedia,
    selectedKeys,
    onSelectedKeysChange,
    tileMinWidth,
    addTile,
    columns
  } = props;
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  const { toastService } = services;
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());
  const [ownSelected, setOwnSelected] = useState<ReadonlySet<string>>(
    new Set()
  );
  const selected = selectedKeys ?? ownSelected;
  const setSelected: Dispatch<SetStateAction<ReadonlySet<string>>> =
    selectedKeys === undefined
      ? setOwnSelected
      : next =>
          onSelectedKeysChange?.(
            typeof next === 'function' ? next(selectedKeys) : next
          );
  const canAdd = onAddFiles !== undefined;
  const sortable = onReorder !== undefined && items.length > 1;
  // To drag several files at once, or for the host's batch menu.
  const canSelect = sortable || selectedKeys !== undefined;
  const isEmpty = items.length === 0 && uploads.length === 0;

  const addFiles = (files: File[], index: number | null) => {
    if (!onAddFiles) return;
    const taken = (multiple ? files : files.slice(0, 1)).filter(file => {
      if (!accepts(accept, file)) {
        toastService.open({
          type: 'error',
          message: t('rejected', { name: file.name })
        });
        return false;
      }
      if (file.size > maxSize) {
        toastService.open({
          type: 'error',
          message: t('tooLarge', {
            name: file.name,
            size: formatSize(maxSize)
          })
        });
        return false;
      }
      return true;
    });
    if (taken.length > 0) onAddFiles(taken, index);
  };

  const remove = onRemove
    ? async (key: string) => {
        setRemoving(prev => new Set(prev).add(key));
        setSelected(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        try {
          await onRemove(key);
        } finally {
          setRemoving(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }
      }
    : undefined;

  const openPicker = useFileInput({
    accept,
    multiple,
    onFilesSelected: files => addFiles(files, null)
  });

  // ⌘V adds files wherever the pointer or the focus is in this gallery, also after the picker
  // closed (focus is then on the page): a paste lands on the focused element, so it listens on
  // the document and asks the area.
  const area = useRef<HTMLDivElement>(null);
  const pointerIn = useRef(false);
  /** The tile the pointer was last over and where on it; read when a paste lands. */
  const pointer = useRef<PointerSpot>({ key: null, clientX: 0 });
  // the listener reads the latest items and adding (it is subscribed once)
  const latest = useRef<PasteTarget>({
    items,
    accepts: () => false,
    add: null
  });
  latest.current = {
    items,
    accepts: file => accepts(accept, file),
    add: canAdd ? (files, index) => addFiles(files, index) : null
  };
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const node = area.current;
      const { items: now, accepts: takes, add } = latest.current;
      if (!node || !add || event.defaultPrevented) return;
      const inside =
        pointerIn.current ||
        node.contains(document.activeElement) ||
        node.contains(event.target as Node);
      if (!inside || isTypingTarget(event.target)) return;
      const files = getFilesFromClipboardData(event.clipboardData).filter(
        takes
      );
      if (files.length === 0) return;
      event.preventDefault();
      add(files, landingIndexAt(pointer.current, now));
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  const formats = accept
    .split(',')
    .map(token => {
      const trimmed = token.trim();
      switch (trimmed) {
        case 'image/*':
          return t('formatImages');
        case 'video/*':
          return t('formatVideos');
        case 'application/pdf':
          return t('formatPdf');
        case '*/*':
          return t('formatAny');
        default:
          return trimmed;
      }
    })
    .join(', ');

  if (isEmpty && !canAdd) {
    return (
      <p className="rounded-lg border border-border-default bg-bg-subtle px-6 py-8 text-center text-sm text-fg-muted">
        {emptyText ?? t('noFiles')}
      </p>
    );
  }

  /** What a tile shows, the finished one and a pending one alike. */
  const mediaOf = (item: MediaGalleryItem): ReactNode =>
    renderMedia ? renderMedia(item) : <MediaPaddedThumbnail item={item} />;

  // one file: the tile replaces it, once there is one (the empty area opens the picker)
  const addTileShown =
    addTile !== undefined && canAdd && (multiple || items.length > 0);
  const adding: Omit<MediaGalleryAdding, 'lastDropLine'> | null = canAdd
    ? { openPicker, multiple }
    : null;

  return (
    <div
      ref={area}
      className="contents"
      onPointerEnter={() => {
        pointerIn.current = true;
      }}
      onPointerMove={event => {
        pointer.current = {
          key: tileKeyAt(event.target),
          clientX: event.clientX
        };
      }}
      onPointerLeave={() => {
        pointerIn.current = false;
        pointer.current = { key: null, clientX: 0 };
      }}
    >
      <TileCollection
        label={t('listLabel')}
        items={items}
        keyOf={item => item.key}
        textOf={item => item.name}
        layout={gridLayout({ tileMinWidth, columns })}
        selection={
          canSelect
            ? {
                // After a drop React Aria picks what came in, here their uploads, and retries for
                // as long as it can't; an upload leaves the picks when it ends.
                selected: new Set(
                  [...selected].filter(
                    key =>
                      items.some(item => item.key === key) ||
                      uploads.some(upload => uploadTileKey(upload.id) === key)
                  )
                ),
                onChange: setSelected,
                // As on the shortlist board, where the host keeps the picks.
                marquee: selectedKeys !== undefined
              }
            : undefined
        }
        reorder={
          onReorder !== undefined
            ? {
                onReorder,
                dragLayer: (item: MediaGalleryItem) =>
                  mediaKindOf(item) === 'image' ? (
                    <DragPicture src={item.src} />
                  ) : (
                    <MediaThumbnail item={item} />
                  )
              }
            : undefined
        }
        incoming={
          canAdd
            ? {
                onDrop: async (dropped, at) =>
                  addFiles(await dropItemFiles(dropped), at)
              }
            : undefined
        }
        onOpen={key => (props.onOpen ? props.onOpen(key) : setPreviewKey(key))}
        onRemove={
          remove ? doomed => doomed.forEach(key => void remove(key)) : undefined
        }
        renderTile={(item, tile) => (
          <ImmersiveCard
            tile={tile}
            media={mediaOf(item)}
            name={item.name}
            caption={item.caption}
            badge={item.badge}
            actions={item.actions}
            emphasized={item.emphasized}
            removing={removing.has(item.key) || item.removing === true}
            onRemove={remove ? () => void remove(item.key) : undefined}
            labels={{
              select: t('select', { name: item.name }),
              reorder: t('reorder', { name: item.name }),
              remove: t('remove', { name: item.name })
            }}
            data-media-key={item.key}
            data-testid="media-tile"
          />
        )}
        trailing={{
          keys: [
            ...uploads.map(upload => uploadTileKey(upload.id)),
            ...(addTileShown ? [ADD_TILE] : [])
          ],
          // The Add tile takes focus and its action, never a place in the selection.
          unpickable: [ADD_TILE],
          render: dropLineAt => (
            <>
              {uploads.map((upload, index) => (
                <PendingTile
                  key={upload.id}
                  upload={upload}
                  // the picture as its finished tile shows it
                  preview={
                    upload.previewUrl
                      ? mediaOf({
                          key: uploadTileKey(upload.id),
                          name: upload.file.name,
                          mimeType: upload.file.type,
                          src: upload.previewUrl,
                          localCopy: true
                        })
                      : undefined
                  }
                  dropLine={dropLineAt(index)}
                  onRetry={() => onRetryUpload(upload)}
                  onDismiss={() => onDismissUpload(upload.id)}
                />
              ))}
              <MediaGalleryAddingContext.Provider
                value={
                  adding && {
                    ...adding,
                    lastDropLine: dropLineAt(uploads.length)
                  }
                }
              >
                {addTile}
              </MediaGalleryAddingContext.Provider>
            </>
          )
        }}
        empty={
          <MediaDropZone
            accept={accept}
            multiple={multiple}
            description={t('formats', { formats, size: formatSize(maxSize) })}
            onFiles={files => addFiles(files, null)}
          />
        }
        className="flex w-full flex-col gap-3"
        data-testid="media-gallery"
      >
        {props.onOpen === undefined && (
          <MediaLightbox
            items={items}
            currentKey={previewKey}
            onCurrentKeyChange={setPreviewKey}
            onRemove={remove ? item => void remove(item.key) : undefined}
            removing={removing}
            thumbnailsShown={props.thumbnailsShown}
            onThumbnailsShownChange={props.onThumbnailsShownChange}
          />
        )}
      </TileCollection>
    </div>
  );
}

/** The tile after the files that picks more; only where the gallery takes several. */
function AddTile() {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  const adding = useContext(MediaGalleryAddingContext);
  if (!adding) return null;
  const label = adding.multiple ? t('add') : t('replace');
  return (
    <GridListItem
      id={ADD_TILE}
      textValue={label}
      onAction={adding.openPicker}
      data-fixed-tile
      className="group/add relative aspect-square min-h-0 min-w-0 cursor-pointer outline-none"
    >
      <motion.span
        layout
        className={cn(
          'flex h-full w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-border-default bg-bg-subtle text-sm text-fg-muted transition-colors',
          'group-data-hovered/add:bg-bg-subtle-2 group-data-hovered/add:text-fg-default',
          'ring-offset-bg-default group-data-focus-visible/add:ring-2 group-data-focus-visible/add:ring-bg-emphasis/70 group-data-focus-visible/add:ring-offset-2'
        )}
      >
        <Plus className="size-5 transition-transform group-data-hovered/add:scale-110" />
        {label}
        {/* the solid frame on hover fades in over the dashed one; the two hints cross-fade */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-lg border-2 border-brand-500 opacity-0 transition-opacity group-data-hovered/add:opacity-100 group-data-focus-visible/add:opacity-100"
        />
        <span className="grid text-center text-xs">
          <span className="col-start-1 row-start-1 text-fg-muted transition-opacity group-data-hovered/add:opacity-0 group-data-focus-visible/add:opacity-0">
            {t('addHint')}
          </span>
          <span className="col-start-1 row-start-1 text-fg-default opacity-0 transition-opacity group-data-hovered/add:opacity-100 group-data-focus-visible/add:opacity-100">
            {t('addHintOnHover')}
          </span>
        </span>
      </motion.span>
      {adding.lastDropLine}
    </GridListItem>
  );
}

/** The gallery; compose `MediaGallery.AddTile` (`addTile`) where files are added from it. */
export const MediaGallery = Object.assign(MediaGalleryRoot, {
  AddTile
});

/**
 * The gallery while its files load: the collection's placeholders in the gallery's layout, each
 * where its tile will stand; as many as `count` (what the host knows will come), or as fill the
 * visible area.
 */
export function MediaGallerySkeleton({
  label,
  count,
  tileMinWidth,
  columns
}: Pick<MediaGalleryProps, 'tileMinWidth' | 'columns'> & {
  /** What loads ("Loading pins"); the gallery's "Loading files" without it. */
  label?: string;
  count?: number;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  return (
    <TileCollectionSkeleton
      label={label ?? t('loading')}
      layout={gridLayout({ tileMinWidth, columns })}
      count={count}
      className="flex w-full flex-col gap-3"
      renderTile={tile => <ImmersiveCardSkeleton tile={tile} />}
    />
  );
}
